import { Editor } from '@ckeditor/ckeditor5-core/src/editor/editor';
import React, { createContext, FC, useReducer } from 'react';
import { Document } from 'api/document/types/document';
import { TaskWithUid } from 'api/tasks';
import { PopupType } from 'data/documents';
import { EditorsObject } from '../ck-collab-editor';
import { TabNames } from './document.types';

type DocumentProviderProps = { children: React.ReactNode };
interface BalloonToolbarDetails {
  type: PopupType;
  position: {
    x: number;
    y: number;
  };
}
export type State = {
  docId?: number;
  documentObj?: Document;
  balloonToolbarDetails: BalloonToolbarDetails;
  editor?: Editor;
  tasks: TaskWithUid[];
  activeTab: TabNames;
  allEditors?: EditorsObject;
};
type Action =
  | { type: 'SET_DOCID'; payload: number }
  | { type: 'SET_DOCUMENT'; payload: Document }
  | { type: 'SET_ACTIVE_EDITOR'; payload: Editor }
  | { type: 'SET_TEXT_SELECTION_CONTENT'; payload: BalloonToolbarDetails }
  | { type: 'UPDATE_ACTIVE_TAB'; payload: TabNames }
  | { type: 'SET_ALL_EDITORS'; payload: EditorsObject };
type Dispatch = (action: Action) => void;
const DocumentStateContext = createContext<State | undefined>(undefined);
const DocumentDispatchContext = createContext<Dispatch | undefined>(undefined);

function documentReducer(state: State, action: Action) {
  switch (action.type) {
    case 'SET_DOCID':
      return { ...state, docId: action.payload };
    case 'SET_DOCUMENT':
      return { ...state, documentObj: action.payload };
    case 'SET_ACTIVE_EDITOR':
      return { ...state, editor: action.payload };
    case 'SET_ALL_EDITORS':
      return {
        ...state,
        allEditors: action.payload,
      };
    case 'SET_TEXT_SELECTION_CONTENT':
      return {
        ...state,
        balloonToolbarDetails: {
          type: action.payload.type,
          position: action.payload.position,
        },
      };
    case 'UPDATE_ACTIVE_TAB':
      return {
        ...state,
        activeTab: action.payload,
      };
    default: {
      throw new Error(`Unhandled action: ${action}`);
    }
  }
}
const DocumentProvider: FC<DocumentProviderProps> = ({
  children,
}: DocumentProviderProps) => {
  const initialState = {
    balloonToolbarDetails: {
      type: PopupType.BalloonToolbar,
      position: {
        x: 0,
        y: 0,
      },
    },
    editor: null,
    tasks: [],
    activeTab: TabNames.ConversationsTab,
  };
  const [state, dispatch] = useReducer(documentReducer, initialState);

  return (
    <DocumentStateContext.Provider value={state}>
      <DocumentDispatchContext.Provider value={dispatch}>
        {children}
      </DocumentDispatchContext.Provider>
    </DocumentStateContext.Provider>
  );
};

// for state and dispatches
function useDocumentsState(): State {
  const context = React.useContext(DocumentStateContext);

  if (context === undefined) {
    throw new Error('useDocumentsState must be used within a DocumentProvider');
  }

  return context;
}
function useDocumentsDispatch(): Dispatch {
  const context = React.useContext(DocumentDispatchContext);

  if (context === undefined) {
    throw new Error(
      'useDocumentsDispatch must be used within a DocumentProvider',
    );
  }

  return context;
}
function useDocuments(): [State, Dispatch] {
  return [useDocumentsState(), useDocumentsDispatch()];
}

const setBalloonToolbarDetails = (
  dispatch: Dispatch,
  details: BalloonToolbarDetails,
): void => {
  dispatch({ type: 'SET_TEXT_SELECTION_CONTENT', payload: details });
};

const setDocumentDetails = (
  dispatch: Dispatch,
  documentObj: Document,
): void => {
  dispatch({ type: 'SET_DOCUMENT', payload: documentObj });
};

const setActiveEditor = (dispatch: Dispatch, editor: Editor): void => {
  dispatch({ type: 'SET_ACTIVE_EDITOR', payload: editor });
};

const saveAllEditors = (dispatch: Dispatch, editors: EditorsObject): void => {
  dispatch({ type: 'SET_ALL_EDITORS', payload: editors });
};

const updateActiveTab = (dispatch: Dispatch, tabName: TabNames): void => {
  dispatch({ type: 'UPDATE_ACTIVE_TAB', payload: tabName });
};

export {
  PopupType,
  DocumentProvider,
  useDocuments,
  setDocumentDetails,
  setActiveEditor,
  saveAllEditors,
  setBalloonToolbarDetails,
  updateActiveTab,
};
